import React from 'react';
import styled, { keyframes } from 'styled-components';

const spinAnim = keyframes`
    to { transform: rotate(360deg); }
`;

/**
 * Skupina šipek (forward + backward) pozicovaná na bottom-center.
 *
 * Vertikální offset reaguje na CSS variable `--pano-pitch` (radián), kterou
 * PanoramaNew updatuje na základě `pano-view` eventu — když uživatel naklání
 * kameru myší, šipky se posunou nahoru/dolů, aby simulovaly perspektivu země
 * podobně jako nativní WebGL šipky Mapy.com.
 */
const NavGroup = styled.div<{ $disabled?: boolean }>`
    position: absolute;
    left: 50%;
    bottom: 64px;
    z-index: 9;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
    opacity: ${props => (props.$disabled ? 0.4 : 1)};
    /* translateX(-50%) horizontálně centruje, druhá složka simuluje perspektivu země */
    transform: translate(-50%, calc(var(--pano-pitch, 0) * -180px));
    transition: opacity 0.2s ease;
    will-change: transform;

    @media (max-width: 600px) {
        bottom: 48px;
    }
`;

const NavButton = styled.button<{ $disabled?: boolean }>`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.95);
    background: rgba(0, 0, 0, 0.35);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: background 0.15s ease, transform 0.08s ease, border-color 0.15s ease;
    padding: 0;

    &:hover {
        background: rgba(0, 0, 0, 0.55);
        border-color: #fff;
    }

    &:active {
        transform: scale(0.92);
    }

    @media (max-width: 600px) {
        width: 36px;
        height: 36px;
    }
`;

const Chevron = styled.svg`
    width: 18px;
    height: 18px;

    @media (max-width: 600px) {
        width: 16px;
        height: 16px;
    }
`;

const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 8;
    pointer-events: none;
`;

const Spinner = styled.div`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: rgba(255, 255, 255, 0.95);
    animation: ${spinAnim} 0.8s linear infinite;
    z-index: 9;
    pointer-events: none;
`;

interface CustomNavigationProps {
    onForward: () => void;
    onBackward: () => void;
    disabled?: boolean;
    isLoading?: boolean;
}

export const CustomNavigation: React.FC<CustomNavigationProps> = ({
    onForward,
    onBackward,
    disabled = false,
    isLoading = false,
}) => {
    const interactionDisabled = disabled || isLoading;

    return (
        <>
            {isLoading && <LoadingOverlay />}
            {isLoading && <Spinner aria-label="Načítám další panorama" />}

            <NavGroup $disabled={interactionDisabled}>
                <NavButton
                    type="button"
                    onClick={onForward}
                    $disabled={interactionDisabled}
                    aria-label="Posunout se dopředu"
                    title="Dopředu (šipka nahoru / W)"
                >
                    <Chevron
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="6 15 12 9 18 15" />
                    </Chevron>
                </NavButton>
                <NavButton
                    type="button"
                    onClick={onBackward}
                    $disabled={interactionDisabled}
                    aria-label="Posunout se dozadu"
                    title="Dozadu (šipka dolů / S)"
                >
                    <Chevron
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </Chevron>
                </NavButton>
            </NavGroup>
        </>
    );
};

export default CustomNavigation;
